import { useState, useEffect, useRef, useCallback } from 'react'
import {
  Phone, Mail, Users, PhoneOff, MessageSquare, Coffee, Film, StickyNote,
  ArrowUpRight, ArrowDownLeft, Sparkles, Loader2, CheckCircle2, X, ChevronDown, ChevronUp, Link2, Plus, Trash2,
} from 'lucide-react'
import { Modal } from '../ui/Modal'
import { createCallLogEntry, findOpenThreadForContact } from '../../hooks/useCallLog'
import { createTask } from '../../hooks/useTasks'
import { structureCallNote } from '../../lib/claudeNoteStructure'
import MultiEntityPicker from '../ui/MultiEntityPicker'
import supabase from '../../lib/supabase'
import { dealLabel } from '../../lib/dealLabel'
import { queuedInsert } from '../../lib/queuedWrite'
import { useToast } from '../../contexts/ToastContext'
import { useUsers } from '../../hooks/useUsers'
import { ACTIVE_DEAL_STAGES } from '../../lib/dealStages'
import { displayDate } from '../../lib/dateUtil.js'

// ── Constants ─────────────────────────────────────────────────────────────────

const TYPE_OPTIONS = [
  { value: 'call',      label: 'Call',      icon: Phone },
  { value: 'email',     label: 'Email',     icon: Mail },
  { value: 'meeting',   label: 'Meeting',   icon: Users },
  { value: 'voicemail', label: 'Voicemail', icon: PhoneOff },
  { value: 'text',      label: 'Text',      icon: MessageSquare },
  { value: 'lunch',     label: 'Lunch',     icon: Coffee },
  { value: 'screening', label: 'Screening', icon: Film },
  { value: 'note',      label: 'Note',      icon: StickyNote },
]

const OUTCOME_OPTIONS = [
  { value: '',              label: 'Select outcome...' },
  { value: 'connected',     label: 'Connected' },
  { value: 'interested',    label: 'Interested' },
  { value: 'follow_up',     label: 'Follow Up' },
  { value: 'meeting_set',   label: 'Meeting Set' },
  { value: 'passed',        label: 'Passed' },
  { value: 'no_answer',     label: 'No Answer' },
  { value: 'left_voicemail',label: 'Left Voicemail' },
]

// ── Helpers ───────────────────────────────────────────────────────────────────

function nowLocal() {
  const now = new Date()
  const offset = now.getTimezoneOffset() * 60000
  return new Date(now - offset).toISOString().slice(0, 16)
}

function defaultTwoWeeks() {
  const d = new Date()
  d.setDate(d.getDate() + 14)
  return d.toISOString().split('T')[0]
}

// Generate a default summary when the user doesn't type notes.
// Examples: "Outbound call — left voicemail", "Inbound email",
// "Meeting — connected", "Text — follow up".
function buildAutoSummary(form, contact) {
  const typeLabel = (TYPE_OPTIONS.find(t => t.value === form.contact_type)?.label || 'Activity')
  const dirLabel = form.direction === 'inbound' ? 'Inbound' : 'Outbound'
  const outcomeLabel = OUTCOME_OPTIONS.find(o => o.value === form.outcome)?.label
  const who = contact ? ` with ${contact.first_name} ${contact.last_name}`.trimEnd() : ''
  const base = `${dirLabel} ${typeLabel.toLowerCase()}${who}`
  return outcomeLabel ? `${base} — ${outcomeLabel.toLowerCase()}` : base
}

// Pull the first phone number from a contact's phones JSONB array
function getPrimaryPhone(contact) {
  if (!contact?.phones) return null
  if (!Array.isArray(contact.phones)) return null
  const primary = contact.phones.find(p => p?.primary) || contact.phones[0]
  return primary?.phone || primary?.number || null
}

function getDefaultForm(contactId) {
  return {
    contact_id:        contactId || null,
    organization_id:   null,
    entity_id:         null,
    contact_date:      nowLocal(),
    contact_type:      'call',
    direction:         'outbound',
    summary:           '',
    outcome:           '',
    follow_up_required: false,
    follow_up_date:    defaultTwoWeeks(),
    follow_up_note:    '',
    linked_contacts:   [],
    linked_deals:      [],
    linked_projects:   [],
    linked_properties: [],
    linked_opportunities: [],
    linked_deal_id:    null,   // active deal negotiation this call is about
    linked_round_id:   null,   // specific round
    tasks:             [],  // array of follow-up task objects (see blankTask())
    _intelligence:     null,  // ephemeral — shown in UI, not saved
  }
}

function blankTask() {
  return {
    _id:          Math.random().toString(36).slice(2),
    title:        '',
    due_date:     defaultTwoWeeks(),
    assignee_id:  null,
    contacts:     [],
    deals:        [],
    projects:     [],
    properties:   [],
    opportunities: [],
  }
}

// ── TasksSection sub-component ────────────────────────────────────────────────

function TaskRow({ task, users, onChange, onRemove, index }) {
  const update = (field, value) => onChange({ ...task, [field]: value })

  return (
    <div className="rounded-lg border border-warm-200 bg-white p-3 space-y-2.5">
      {/* Title row */}
      <div className="flex items-center gap-2">
        <span className="text-[10px] font-semibold text-warm-400 uppercase tracking-wide shrink-0">Task {index + 1}</span>
        <input
          className="input-field flex-1 min-w-0 text-sm"
          placeholder="What needs to happen?"
          value={task.title}
          onChange={e => update('title', e.target.value)}
        />
        <button onClick={onRemove} className="text-warm-300 hover:text-red-400 transition-colors shrink-0 p-0.5">
          <Trash2 size={14} />
        </button>
      </div>

      {/* Assigned to / Follow up with / Due */}
      <div className="grid grid-cols-1 sm:grid-cols-[1fr_1fr_auto] gap-2 sm:items-end">
        <div>
          <label className="field-label text-[10px]">Assigned to</label>
          <select
            className="input-field text-sm"
            value={task.assignee_id || ''}
            onChange={e => update('assignee_id', e.target.value || null)}
          >
            <option value="">— Me —</option>
            {users.map(u => (
              <option key={u.id} value={u.id}>{u.full_name || u.email}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="field-label text-[10px]">Follow up with</label>
          <MultiEntityPicker
            table="contacts"
            placeholder="Contact..."
            value={task.contacts}
            onChange={v => update('contacts', v)}
            searchCols={['first_name', 'last_name']}
            labelFn={r => [r.first_name, r.last_name].filter(Boolean).join(' ')}
            selectCols="id,first_name,last_name"
            compact
          />
        </div>
        <div>
          <label className="field-label text-[10px]">Due</label>
          <input type="date" className="input-field w-full sm:w-32 text-sm"
            value={task.due_date}
            onChange={e => update('due_date', e.target.value)} />
        </div>
      </div>

      <div>
        <label className="field-label text-[10px]">Re: <span className="text-warm-400 font-normal normal-case">inherits from Discussed if left blank</span></label>
        <div className="grid grid-cols-2 gap-1.5 mt-1">
          <MultiEntityPicker table="deals" placeholder="Deal..." value={task.deals}
            onChange={v => update('deals', v)} searchCols={['title']} labelFn={r => r.title} selectCols="id,title" compact />
          <MultiEntityPicker table="projects" placeholder="Project..." value={task.projects}
            onChange={v => update('projects', v)} searchCols={['title']} labelFn={r => r.title} selectCols="id,title" compact />
          <MultiEntityPicker table="properties" placeholder="Property..." value={task.properties}
            onChange={v => update('properties', v)} searchCols={['title']} labelFn={r => r.title} selectCols="id,title" compact />
          <MultiEntityPicker table="opportunities" placeholder="Opportunity..." value={task.opportunities}
            onChange={v => update('opportunities', v)} searchCols={['title']} labelFn={r => r.title} selectCols="id,title" compact />
        </div>
      </div>
    </div>
  )
}

function TasksSection({ tasks, onChange }) {
  const { users } = useUsers()

  const updateTask = (index, updated) => {
    const next = [...tasks]
    next[index] = updated
    onChange(next)
  }

  const removeTask = (index) => {
    onChange(tasks.filter((_, i) => i !== index))
  }

  const addTask = () => onChange([...tasks, blankTask()])

  return (
    <div className="space-y-2">
      {tasks.map((task, i) => (
        <TaskRow
          key={task._id}
          task={task}
          users={users}
          index={i}
          onChange={updated => updateTask(i, updated)}
          onRemove={() => removeTask(i)}
        />
      ))}
      <button
        type="button"
        onClick={addTask}
        className="flex items-center gap-1.5 text-sm text-navy-600 hover:text-navy-800 font-medium transition-colors py-1"
      >
        <Plus size={14} />
        Add follow-up task
      </button>
    </div>
  )
}

// ── Component ─────────────────────────────────────────────────────────────────

export default function QuickLogModal({ isOpen, onClose, defaultContactId = null, continueFromEntry = null, onLogged }) {
  const toast = useToast()
  const [form, setForm]           = useState(getDefaultForm(defaultContactId))
  const [contactSearch, setContactSearch] = useState('')
  const [contactResults, setContactResults] = useState([])
  const [selectedContact, setSelectedContact] = useState(null)
  const [showDropdown, setShowDropdown] = useState(false)
  const [structuring, setStructuring] = useState(false)
  const [saving, setSaving]       = useState(false)
  const [saved, setSaved]         = useState(false)
  const [showLinks, setShowLinks] = useState(false)
  const [activeDeals, setActiveDeals] = useState([])
  const [dealRounds, setDealRounds] = useState([])
  // Phone-tag threading: if non-null, the new entry joins an existing thread
  const [threadContext, setThreadContext] = useState(null) // { thread_id, latest, attempt_count }
  const summaryRef = useRef(null)

  // Reset on open
  useEffect(() => {
    if (isOpen) {
      setForm(getDefaultForm(defaultContactId))
      setContactSearch('')
      setSelectedContact(null)
      setSaved(false)
      setContactResults([])
      // If continuing an existing entry, lock to that thread
      if (continueFromEntry) {
        setThreadContext({
          thread_id: continueFromEntry.thread_id || continueFromEntry.id,
          latest: continueFromEntry,
          attempt_count: null, // unknown here, banner just says "Continuing"
        })
        // Suggest flipping direction for natural phone-tag flow
        setForm(f => ({
          ...f,
          contact_id: continueFromEntry.contact_id,
          direction: continueFromEntry.direction === 'outbound' ? 'inbound' : 'outbound',
          contact_type: continueFromEntry.contact_type || 'call',
        }))
      } else {
        setThreadContext(null)
      }
    }
  }, [isOpen, defaultContactId, continueFromEntry])

  // Pre-fill contact search if defaultContactId provided
  useEffect(() => {
    if (isOpen && defaultContactId) {
      supabase
        .from('contacts')
        .select('id,first_name,last_name,title,phones,organization:companies(id,name)')
        .eq('id', defaultContactId)
        .single()
        .then(({ data }) => {
          if (data) {
            setSelectedContact(data)
            setContactSearch(`${data.first_name} ${data.last_name}`)
          }
        })
    }
  }, [isOpen, defaultContactId])

  // Debounced contact search
  useEffect(() => {
    if (!contactSearch.trim() || contactSearch.length < 2 || selectedContact) {
      setContactResults([])
      setShowDropdown(false)
      return
    }
    const t = setTimeout(async () => {
      const words = contactSearch.trim().split(/\s+/).filter(Boolean)
      let ids = null
      for (const word of words) {
        const { data: wd } = await supabase
          .from('contacts')
          .select('id')
          .or(`first_name.ilike.%${word}%,last_name.ilike.%${word}%`)
          .limit(200)
        const wordIds = new Set((wd || []).map(r => r.id))
        ids = ids === null ? wordIds : new Set([...ids].filter(id => wordIds.has(id)))
      }
      if (!ids || ids.size === 0) { setContactResults([]); setShowDropdown(true); return }
      const { data } = await supabase
        .from('contacts')
        .select('id,first_name,last_name,title,phones,organization:companies(id,name)')
        .in('id', [...ids])
        .limit(6)
      setContactResults(data || [])
      setShowDropdown(true)
    }, 220)
    return () => clearTimeout(t)
  }, [contactSearch, selectedContact])

  const selectContact = async (c) => {
    setSelectedContact(c)
    setContactSearch(`${c.first_name} ${c.last_name}`)
    setContactResults([])
    setShowDropdown(false)
    setForm(f => ({
      ...f,
      contact_id:      c.id,
      organization_id: c.organization?.id || f.organization_id,
    }))
    setTimeout(() => summaryRef.current?.focus(), 80)
    // Check for open phone-tag thread with this contact (unless we're already
    // locked to a thread via continueFromEntry)
    if (!continueFromEntry) {
      try {
        const open = await findOpenThreadForContact(c.id)
        if (open) setThreadContext(open)
      } catch { /* non-blocking */ }
    }
  }

  const clearContact = () => {
    setSelectedContact(null)
    setContactSearch('')
    setForm(f => ({ ...f, contact_id: null, organization_id: null, linked_deal_id: null, linked_round_id: null }))
    setActiveDeals([])
    setDealRounds([])
    if (!continueFromEntry) setThreadContext(null)
  }

  const dismissThread = () => setThreadContext(null)

  // Load active deals (optionally filtered by contact) — simple: fetch all active deals
  useEffect(() => {
    if (!isOpen) return
    let cancelled = false
    ;(async () => {
      const { data } = await supabase
        .from('deals')
        .select('id, title, deal_stage, deal_type, talent_name, talent_type, studio_name, counterpart, counterparty_contact_id, client_contact_id')
        // Negotiation-lifecycle stages OR stage not set (unknown ≠ excluded —
        // a NULL-stage deal may well be the one this call is about); archived
        // records stay out. The old `.in(ACTIVE_DEAL_STAGES)` filter showed a
        // near-empty picker for years: every defaulted stage failed it.
        .or(`deal_stage.in.(${ACTIVE_DEAL_STAGES.join(',')}),deal_stage.is.null`)
        .neq('record_state', 'archived')
        .order('updated_at', { ascending: false })
        .limit(100)
      if (cancelled) return
      let list = data || []
      // If a contact is selected, prefer deals where that contact is linked; fallback to all
      if (form.contact_id) {
        const filtered = list.filter(
          (d) => d.counterparty_contact_id === form.contact_id || d.client_contact_id === form.contact_id
        )
        list = filtered.length > 0 ? filtered : list
      }
      setActiveDeals(list)
    })()
    return () => { cancelled = true }
  }, [isOpen, form.contact_id])

  // Load rounds when a deal is picked
  useEffect(() => {
    if (!form.linked_deal_id) { setDealRounds([]); return }
    let cancelled = false
    ;(async () => {
      const { data } = await supabase
        .from('deal_negotiation_rounds')
        .select('id, round_number, round_label, party, created_at')
        .eq('deal_id', form.linked_deal_id)
        .order('round_number', { ascending: true })
      if (!cancelled) setDealRounds(data || [])
    })()
    return () => { cancelled = true }
  }, [form.linked_deal_id])

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }))

  // Claude note structuring
  const handleStructure = async () => {
    if (!form.summary.trim() || structuring) return
    setStructuring(true)
    try {
      const result = await structureCallNote(form.summary)
      if (result) {
        setForm(f => ({
          ...f,
          summary:           result.summary           || f.summary,
          contact_type:      result.contact_type      || f.contact_type,
          direction:         result.direction         || f.direction,
          outcome:           result.outcome           || f.outcome,
          follow_up_required: result.follow_up_required ?? f.follow_up_required,
          follow_up_date:    result.follow_up_date    || f.follow_up_date,
          follow_up_note:    result.follow_up_note    || f.follow_up_note,
          _intelligence:     result.intelligence_notes || null,
        }))
      }
    } finally {
      setStructuring(false)
    }
  }

  const handleSubmit = async () => {
    if (saving) return
    // Auto-generate a summary if notes are blank — "Left voicemail", "Spoke",
    // "Sent email", etc. This way a one-click log works with just the type/outcome.
    const autoSummary = form.summary.trim() || buildAutoSummary(form, selectedContact)
    if (!form.contact_id && !autoSummary) return
    setSaving(true)
    try {
      const payload = {
        entity_id:         form.entity_id         || null,
        contact_id:        form.contact_id         || null,
        contact_name:      selectedContact ? `${selectedContact.first_name} ${selectedContact.last_name}`.trim() : null,
        organization_id:   form.organization_id    || null,
        organization_name: selectedContact?.organization?.name || null,
        contact_date:      new Date(form.contact_date).toISOString(),
        contact_type:      form.contact_type,
        direction:         form.direction,
        summary:           autoSummary,
        thread_id:         threadContext?.thread_id || undefined,
        outcome:           form.outcome            || null,
        follow_up_required: form.follow_up_required,
        follow_up_date:    form.follow_up_required ? (form.follow_up_date || null) : null,
        follow_up_note:    form.follow_up_required ? (form.follow_up_note.trim() || null) : null,
        linked_contacts:      form.linked_contacts.length > 0 ? form.linked_contacts : null,
        linked_deals:         form.linked_deals.length > 0 ? form.linked_deals : null,
        linked_projects:      form.linked_projects.length > 0 ? form.linked_projects : null,
        linked_properties:    form.linked_properties.length > 0 ? form.linked_properties : null,
        linked_opportunities: form.linked_opportunities.length > 0 ? form.linked_opportunities : null,
      }
      const entry = await createCallLogEntry(payload)

      // Create all follow-up tasks
      const validTasks = form.tasks.filter(t => t.title.trim())
      for (const t of validTasks) {
        // Fall back to call's discussed entities when the task has none specified
        const contacts     = t.contacts.length     > 0 ? t.contacts     : (form.contact_id ? [{ id: form.contact_id, label: payload.contact_name }] : [])
        const deals        = t.deals.length        > 0 ? t.deals        : form.linked_deals
        const projects     = t.projects.length     > 0 ? t.projects     : form.linked_projects
        const properties   = t.properties.length   > 0 ? t.properties   : form.linked_properties
        const opportunities = t.opportunities.length > 0 ? t.opportunities : form.linked_opportunities
        await createTask({
          title:                t.title.trim(),
          due_date:             t.due_date || null,
          assigned_to:          t.assignee_id || null,
          status:               'pending',
          priority:             2,   // 1 = top, 2 = active/medium, 3 = general/low
          linked_contacts:      contacts,
          linked_deals:         deals,
          linked_projects:      projects,
          linked_properties:    properties,
          linked_opportunities: opportunities,
        })
      }

      setSaved(true)
      onLogged?.(entry)
      setTimeout(onClose, 1400)
    } catch (e) {
      toast.error(e.message)
    } finally {
      setSaving(false)
    }
  }

  // ── Saved confirmation ──
  if (saved) return (
    <Modal isOpen={isOpen} onClose={onClose} title="" size="sm">
      <div className="flex flex-col items-center justify-center py-12 gap-3">
        <div className="w-14 h-14 rounded-full bg-sage-100 flex items-center justify-center">
          <CheckCircle2 size={28} className="text-sage-500" />
        </div>
        <p className="font-display font-semibold text-warm-800">Activity Logged</p>
        <p className="text-sm text-warm-500">
          {selectedContact ? `Added to ${selectedContact.first_name}'s Activity View` : 'Entry saved to Activity Feed'}
        </p>
      </div>
    </Modal>
  )

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Log Activity" size="lg">
      <div className="p-6 space-y-5">

        {/* ── Contact autocomplete ─────────────────────────────── */}
        <div className="relative">
          <label className="field-label">Contact</label>
          <div className="relative">
            <input
              className="input-field pr-8"
              placeholder="Search by name..."
              value={contactSearch}
              onChange={e => {
                setContactSearch(e.target.value)
                if (selectedContact) setSelectedContact(null)
                setForm(f => ({ ...f, contact_id: null }))
              }}
              onFocus={() => contactResults.length > 0 && setShowDropdown(true)}
              onBlur={() => setTimeout(() => setShowDropdown(false), 150)}
            />
            {selectedContact && (
              <button onClick={clearContact}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-warm-400 hover:text-warm-600 transition-colors">
                <X size={14} />
              </button>
            )}
          </div>
          {showDropdown && contactResults.length > 0 && (
            <div className="absolute top-full left-0 right-0 z-50 mt-1 bg-white border border-warm-200 rounded-xl shadow-lg overflow-hidden">
              {contactResults.map(c => {
                const phones = Array.isArray(c.phones) ? c.phones : []
                return (
                  <div key={c.id}
                    className="border-b border-warm-100 last:border-b-0 hover:bg-warm-50 transition-colors">
                    <button onMouseDown={() => selectContact(c)}
                      className="w-full flex items-center gap-3 px-3 py-2 text-left">
                      <div className="w-7 h-7 rounded-full bg-navy-50 border border-navy-100 flex items-center justify-center flex-shrink-0">
                        <span className="text-[10px] font-bold text-navy-500">{c.first_name[0]}{c.last_name[0]}</span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-warm-800">{c.first_name} {c.last_name}</p>
                        {c.title && <p className="text-xs text-warm-400 truncate">{c.title}{c.organization ? ` · ${c.organization.name}` : ''}</p>}
                      </div>
                    </button>
                    {phones.length > 0 && (
                      <div className="px-3 pb-2 flex flex-wrap gap-1.5 pl-12">
                        {phones.slice(0, 3).map((p, i) => {
                          const num = p?.phone || p?.number
                          if (!num) return null
                          return (
                            <a key={i} href={`tel:${num}`}
                              onMouseDown={e => e.stopPropagation()}
                              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-navy-50 hover:bg-navy-100 border border-navy-200 text-[11px] text-navy-700 font-mono">
                              <Phone size={9} /> {num}
                              {p.type && <span className="text-warm-500 ml-0.5">· {p.type}</span>}
                            </a>
                          )
                        })}
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          )}
          {selectedContact && Array.isArray(selectedContact.phones) && selectedContact.phones.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-1.5">
              {selectedContact.phones.map((p, i) => {
                const num = p?.phone || p?.number
                if (!num) return null
                return (
                  <a key={i} href={`tel:${num}`}
                    className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md bg-navy-50 hover:bg-navy-100 border border-navy-200 text-xs text-navy-700 font-mono">
                    <Phone size={11} /> {num}
                    {p.type && <span className="text-warm-500 ml-0.5">· {p.type}</span>}
                  </a>
                )
              })}
            </div>
          )}
        </div>

        {/* ── Phone-tag thread banner ─────────────────────────────── */}
        {threadContext && (
          <div className="flex items-start gap-3 px-3 py-2.5 rounded-lg bg-amber-50 border border-amber-200">
            <Link2 size={14} className="text-amber-600 mt-0.5 flex-shrink-0" />
            <div className="flex-1 min-w-0 text-xs">
              <p className="font-medium text-amber-900">
                {continueFromEntry ? 'Continuing thread' : 'Open phone-tag thread'}
              </p>
              <p className="text-amber-700 mt-0.5">
                {threadContext.latest && (
                  <>
                    Last: <span className="font-medium">{threadContext.latest.direction === 'inbound' ? 'Inbound' : 'Outbound'} {threadContext.latest.contact_type}</span>
                    {threadContext.latest.outcome && <> — {threadContext.latest.outcome.replace(/_/g, ' ')}</>}
                    {threadContext.latest.created_at && <> · {displayDate(threadContext.latest.created_at).toLocaleString()}</>}
                  </>
                )}
                {threadContext.attempt_count > 1 && <> · {threadContext.attempt_count} attempts</>}
              </p>
              <p className="text-amber-600 mt-1 italic">This entry will be added to the chain.</p>
            </div>
            {!continueFromEntry && (
              <button onClick={dismissThread}
                className="text-amber-500 hover:text-amber-700 flex-shrink-0"
                title="Start a new thread instead">
                <X size={14} />
              </button>
            )}
          </div>
        )}

        {/* ── Type selector ───────────────────────────────────────── */}
        <div>
          <label className="field-label">Type</label>

          {/* Mobile: compact pill row */}
          <div className="flex md:hidden flex-wrap gap-1.5">
            {TYPE_OPTIONS.map(({ value, label, icon: Icon }) => (
              <button key={value} onClick={() => set('contact_type', value)}
                className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium border transition-colors ${
                  form.contact_type === value
                    ? 'bg-navy-500 text-white border-navy-500'
                    : 'bg-warm-50 text-warm-600 border-warm-200 hover:border-navy-300 hover:text-navy-600'
                }`}>
                <Icon size={11} /> {label}
              </button>
            ))}
          </div>

          {/* Desktop: elegant 4-column card grid */}
          <div className="hidden md:grid grid-cols-4 gap-2">
            {TYPE_OPTIONS.map(({ value, label, icon: Icon }) => (
              <button key={value} onClick={() => set('contact_type', value)}
                className={`flex flex-col items-center gap-2 py-4 rounded-xl border transition-all duration-150 ${
                  form.contact_type === value
                    ? 'bg-navy-500 text-white border-navy-500 shadow-sm ring-2 ring-navy-200'
                    : 'bg-white text-warm-500 border-warm-200 hover:border-navy-200 hover:bg-navy-50 hover:text-navy-600'
                }`}>
                <Icon size={20} />
                <span className="text-[11px] font-medium tracking-wide">{label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* ── Direction + Date ──────────────────────────────────────── */}
        {['note', 'screening'].includes(form.contact_type) ? (
          <div>
            <label className="field-label">Date & Time</label>
            <input type="datetime-local" className="input-field"
              value={form.contact_date}
              onChange={e => set('contact_date', e.target.value)} />
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="field-label">Direction</label>
              <div className="flex gap-2">
                {[
                  { value: 'outbound', label: 'Outbound', Icon: ArrowUpRight },
                  { value: 'inbound',  label: 'Inbound',  Icon: ArrowDownLeft },
                ].map(({ value, label, Icon }) => (
                  <button key={value} onClick={() => set('direction', value)}
                    className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium border transition-colors ${
                      form.direction === value
                        ? 'bg-navy-500 text-white border-navy-500'
                        : 'bg-warm-50 text-warm-600 border-warm-200 hover:border-navy-300'
                    }`}>
                    <Icon size={13} /> {label}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="field-label">Date & Time</label>
              <input type="datetime-local" className="input-field"
                value={form.contact_date}
                onChange={e => set('contact_date', e.target.value)} />
            </div>
          </div>
        )}

        {/* ── Notes + Claude ────────────────────────────────────────── */}
        <div>
          <div className="flex items-center justify-between mb-1">
            <label className="field-label" style={{ marginBottom: 0 }}>Notes</label>
            <button onClick={handleStructure}
              disabled={!form.summary.trim() || structuring}
              className="flex items-center gap-1.5 text-xs text-navy-500 hover:text-navy-700 disabled:opacity-40 transition-colors font-medium">
              {structuring
                ? <><Loader2 size={11} className="animate-spin" /> Structuring…</>
                : <><Sparkles size={11} /> Structure with Claude</>}
            </button>
          </div>
          <textarea
            ref={summaryRef}
            className="input-field resize-none"
            rows={3}
            placeholder="e.g. talked to Marcus at Netflix, Sarah focused on adult animation not kids, follow up Q2 about Witch Hazel"
            value={form.summary}
            onChange={e => set('summary', e.target.value)}
            autoFocus
          />
          {form._intelligence && (
            <div className="mt-2 flex items-start gap-2 px-3 py-2 bg-navy-50 border border-navy-100 rounded-lg">
              <Sparkles size={11} className="text-navy-400 mt-0.5 flex-shrink-0" />
              <p className="text-xs text-navy-700 flex-1">{form._intelligence}</p>
              <button onClick={() => set('_intelligence', null)}>
                <X size={12} className="text-navy-400 hover:text-navy-600" />
              </button>
            </div>
          )}
        </div>

        {/* ── Outcome ───────────────────────────────────────────────── */}
        <div>
          <label className="field-label">Outcome</label>
          <select className="input-field" value={form.outcome} onChange={e => set('outcome', e.target.value)}>
            {OUTCOME_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </div>

        {/* ── Follow-up ─────────────────────────────────────────────── */}
        <div className="rounded-xl border border-warm-200 bg-warm-50 p-3.5">
          <label className="flex items-center gap-2.5 cursor-pointer select-none">
            <input type="checkbox" checked={form.follow_up_required}
              onChange={e => set('follow_up_required', e.target.checked)}
              className="w-4 h-4 rounded border-warm-300 accent-navy-500" />
            <span className="text-sm font-medium text-warm-800">Follow-up required</span>
          </label>
          {form.follow_up_required && (
            <div className="mt-3 grid grid-cols-2 gap-3">
              <div>
                <label className="field-label">Follow-up Date</label>
                <input type="date" className="input-field"
                  value={form.follow_up_date}
                  onChange={e => set('follow_up_date', e.target.value)} />
              </div>
              <div>
                <label className="field-label">What's the follow-up?</label>
                <input className="input-field" placeholder="e.g. Check on Witch Hazel interest"
                  value={form.follow_up_note}
                  onChange={e => set('follow_up_note', e.target.value)} />
              </div>
            </div>
          )}
        </div>

        {/* ── Active Deal Negotiation Link ──────────────────────────── */}
        {activeDeals.length > 0 && (
          <div className="rounded-xl border border-warm-200 bg-warm-50 p-3.5">
            <label className="flex items-center gap-2 text-xs font-semibold text-warm-700 uppercase tracking-wide mb-2">
              <Link2 size={11} />
              Link to active deal negotiation <span className="text-[10px] font-normal text-warm-400 normal-case">(optional)</span>
            </label>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="field-label">Deal</label>
                <select
                  className="input-field"
                  value={form.linked_deal_id || ''}
                  onChange={e => setForm(f => ({ ...f, linked_deal_id: e.target.value || null, linked_round_id: null }))}
                >
                  <option value="">— None —</option>
                  {activeDeals.map(d => (
                    <option key={d.id} value={d.id}>{dealLabel(d, { context: 'global' })} · {d.deal_stage || 'stage not set'}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="field-label">Round</label>
                <select
                  className="input-field"
                  value={form.linked_round_id || ''}
                  disabled={!form.linked_deal_id || dealRounds.length === 0}
                  onChange={e => set('linked_round_id', e.target.value || null)}
                >
                  <option value="">{form.linked_deal_id ? (dealRounds.length === 0 ? 'No rounds yet' : '— Any round —') : '— Pick a deal —'}</option>
                  {dealRounds.map(r => (
                    <option key={r.id} value={r.id}>
                      {r.round_label || `Round ${r.round_number}`}{r.party ? ` · ${r.party}` : ''}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        )}

        {/* ── Links ────────────────────────────────────────────────── */}
        <div className="rounded-xl border border-warm-200 bg-warm-50 overflow-hidden">
          <button
            type="button"
            className="w-full flex items-center justify-between px-3.5 py-2.5 text-left"
            onClick={() => setShowLinks(v => !v)}
          >
            <span className="flex items-center gap-2 text-xs font-semibold text-warm-700 uppercase tracking-wide">
              <Link2 size={11} />
              Discussed
              {(form.linked_contacts.length + form.linked_deals.length + form.linked_projects.length + form.linked_properties.length + form.linked_opportunities.length) > 0 && (
                <span className="px-1.5 py-0.5 bg-navy-100 text-navy-600 rounded-full text-[10px]">
                  {form.linked_contacts.length + form.linked_deals.length + form.linked_projects.length + form.linked_properties.length + form.linked_opportunities.length}
                </span>
              )}
            </span>
            {showLinks ? <ChevronUp size={13} className="text-warm-400" /> : <ChevronDown size={13} className="text-warm-400" />}
          </button>
          {showLinks && (
            <div className="px-3.5 pb-3.5 space-y-3 border-t border-warm-200 pt-3">
              <div>
                <label className="field-label mb-1">Contacts / Clients</label>
                <MultiEntityPicker
                  value={form.linked_contacts}
                  onChange={v => set('linked_contacts', v)}
                  table="contacts"
                  searchCols={['first_name', 'last_name']}
                  labelFn={r => [r.first_name, r.last_name].filter(Boolean).join(' ')}
                  selectCols="id, first_name, last_name"
                  placeholder="Search contacts..."
                  onCreate={async (query) => {
                    const parts = query.trim().split(/\s+/)
                    const data = await queuedInsert('contacts', {
                      first_name: parts[0] || query,
                      last_name: parts.slice(1).join(' ') || '',
                    })
                    return { id: data.id, label: [data.first_name, data.last_name].filter(Boolean).join(' '), ...data }
                  }}
                />
              </div>
              <div>
                <label className="field-label mb-1">Deals</label>
                <MultiEntityPicker
                  value={form.linked_deals}
                  onChange={v => set('linked_deals', v)}
                  table="deals"
                  searchCols={['title']}
                  labelFn={r => r.title}
                  selectCols="id, title"
                  placeholder="Search deals..."
                  onCreate={async (query) => {
                    const data = await queuedInsert('deals', { title: query, status: 'tracking', company_id: 'chatrone_us' })
                    return { id: data.id, label: data.title, ...data }
                  }}
                />
              </div>
              <div>
                <label className="field-label mb-1">Projects</label>
                <MultiEntityPicker
                  value={form.linked_projects}
                  onChange={v => set('linked_projects', v)}
                  table="projects"
                  searchCols={['title']}
                  labelFn={r => r.title}
                  selectCols="id, title"
                  placeholder="Search projects..."
                  onCreate={async (query) => {
                    const data = await queuedInsert('projects', { title: query, status: 'development', company_id: 'chatrone_us' })
                    return { id: data.id, label: data.title, ...data }
                  }}
                />
              </div>
              <div>
                <label className="field-label mb-1">Properties</label>
                <MultiEntityPicker
                  value={form.linked_properties}
                  onChange={v => set('linked_properties', v)}
                  table="properties"
                  searchCols={['title']}
                  labelFn={r => r.title}
                  selectCols="id, title"
                  placeholder="Search properties..."
                  onCreate={async (query) => {
                    const data = await queuedInsert('properties', { title: query, status: 'available', company_id: 'chatrone_us' })
                    return { id: data.id, label: data.title, ...data }
                  }}
                />
              </div>
              <div>
                <label className="field-label mb-1">Opportunities</label>
                <MultiEntityPicker
                  value={form.linked_opportunities}
                  onChange={v => set('linked_opportunities', v)}
                  table="opportunities"
                  searchCols={['title']}
                  labelFn={r => r.title}
                  selectCols="id, title"
                  placeholder="Search opportunities..."
                  onCreate={async (query) => {
                    const data = await queuedInsert('opportunities', { title: query, status: 'tracking', opportunity_type: 'feature' })
                    return { id: data.id, label: data.title, ...data }
                  }}
                />
              </div>
            </div>
          )}
        </div>

        {/* ── Follow-up Tasks ──────────────────────────────────────── */}
        <TasksSection
          tasks={form.tasks}
          onChange={tasks => set('tasks', tasks)}
          defaultContacts={form.contact_id ? [] : []}
        />

        {/* ── Actions ───────────────────────────────────────────────── */}
        <div className="flex justify-end gap-3 pt-1">
          <button onClick={onClose} className="btn-secondary">Cancel</button>
          <button onClick={handleSubmit}
            disabled={!form.contact_id || saving}
            className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed">
            {saving
              ? <><Loader2 size={14} className="animate-spin" /> Saving…</>
              : <><Phone size={14} /> Log Activity</>}
          </button>
        </div>

      </div>
    </Modal>
  )
}
